// Auth context — supports BOTH JWT email/password AND Emergent Google OAuth.
// Backend exposes /api/auth/login, /register, /me, /logout, /google/session.
// Cookies are httpOnly so we never touch tokens client-side — fetch with `credentials: 'include'`.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

const apiCall = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
  if (!r.ok) {
    const text = await r.text();
    let detail = text;
    try { detail = JSON.parse(text).detail || text; } catch (_) { /* keep text */ }
    if (Array.isArray(detail)) detail = detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
    throw new Error(typeof detail === 'string' ? detail : 'Request failed');
  }
  return r.json();
};

export const AuthProvider = ({ children }) => {
  // null = checking, false = anonymous, object = authenticated
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const u = await apiCall('/api/auth/me');
      if (u && u.user_id) { setUser(u); return u; }
      setUser(false);
      return null;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // If we're returning from Google OAuth (hash has session_id), let the
    // callback page handle the exchange first — skip /me check.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setUser(null);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const u = await apiCall('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const u = await apiCall('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    setUser(u);
    return u;
  }, []);

  const exchangeGoogle = useCallback(async (session_id) => {
    const u = await apiCall('/api/auth/google/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id }),
    });
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await apiCall('/api/auth/logout', { method: 'POST' }); } catch (_) { /* ignore */ }
    setUser(false);
  }, []);

  const value = { user, login, register, logout, exchangeGoogle, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
